'use client'

import { useEffect, useRef, useState } from 'react'

// Shared helpers for the start page's live product demos.

export function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export function useInView(ref, threshold = 0.35) {
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [ref, threshold])
  return inView
}

// Eases a number towards its new value so totals count instead of jumping.
export function useTween(value, duration = 700) {
  const [shown, setShown] = useState(value)
  const shownRef = useRef(value)
  useEffect(() => {
    const from = shownRef.current
    if (from === value) return
    const start = performance.now()
    let frame
    const step = now => {
      const t = Math.min(1, (now - start) / duration)
      const next = from + (value - from) * (1 - Math.pow(1 - t, 3))
      shownRef.current = next
      setShown(next)
      if (t < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [value, duration])
  return shown
}

// Steps through `phases` ({ hold } in ms) while `active`, then continues from `loopFrom`.
// Holds the current phase while `pausedRef.current` is true (pointer over the demo).
// Returns phase 0, the resting state, when inactive or with reduced motion.
export function useDemoLoop(phases, { active, pausedRef, loopFrom = 0 }) {
  const [index, setIndex] = useState(0)
  useEffect(() => {
    if (!active || prefersReducedMotion()) {
      setIndex(0)
      return
    }
    let current = 0
    let timer
    const schedule = () => {
      timer = setTimeout(() => {
        if (!pausedRef?.current) {
          current = current + 1 < phases.length ? current + 1 : loopFrom
          setIndex(current)
        }
        schedule()
      }, phases[current].hold)
    }
    schedule()
    return () => clearTimeout(timer)
  }, [active, phases, loopFrom, pausedRef])
  return index
}
